import { useTranslation } from 'react-i18next';

import { useCageOptional } from '@/contexts/cage';
import { usePenCohortName } from '@/contexts/pen-cohort-context';
import { isLandbasedTank, isSmoltProductionTank } from '@/utils/locationTypeHelpers';

/**
 * A page title with the cohort appended, when the title *is* a pen's name.
 *
 * `"C04"` becomes `"Pen C04 - FG-C04-26A"`. Anything else is returned untouched.
 *
 * Applied in `PageHeader` rather than at every call site on purpose. Around
 * thirty screens title themselves with the cage name, several of them indirectly
 * through a shared wrapper, and decorating them one by one both churns thirty
 * files and reliably misses a few, since a sweep over `title={cageName}` skips
 * the indirect ones. It is a layering compromise: a shared layout component ends
 * up knowing about cages. Worth it while the feature is a concept; the honest fix
 * is an explicit prop once the shape settles.
 *
 * Three things make it safe:
 *
 * - **It only fires on the pen's name.** Comparing against the cage context's
 *   name is what keeps `"Fish Speed"` or `"Wounds"` from being decorated as
 *   though they were pens.
 * - **It cannot throw.** Both sources are plain `useContext` reads that return
 *   `undefined` outside their providers, so every page that is not a cage page —
 *   and every test that renders `PageLayout` bare — is untouched. The work
 *   that needs a Router and a query client happens in `PenCohortProvider`.
 * - **The feature flag needs no check.** The cohort name is only ever resolved
 *   when the overview was asked for cohort data, which only happens with
 *   `fish-group-tracking` on. Flag off, and every title is byte-identical.
 */
export function useCohortDecoratedTitle(title?: string): string | undefined {
    const cageContext = useCageOptional();
    const cohortName = usePenCohortName();
    const { t } = useTranslation();

    if (!title || !cohortName || title !== cageContext?.cage?.name) {
        return title;
    }

    // The generic label the rest of the portal uses; a smolt or land-based unit is
    // a tank, not a pen. Derived from the cage type through plain helpers rather
    // than `useIsSmolt`/`useIsLandbased`, which throw outside a `CageProvider`.
    const cageType = cageContext.cage?.cageType;
    const genericName =
        isSmoltProductionTank(cageType) || isLandbasedTank(cageType)
            ? 'common.labels.tank'
            : 'common.labels.cage';

    return `${t(genericName)} ${title} - ${cohortName}`;
}
